import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { captureException } from "@sentry/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useLidarClearance } from "./LidarClearanceContext";
import StopValueFields from "./StopValueFields";
import { createColorScale } from "./api";
import t from "./translations";
import { newStopRow, useStopRows } from "./useStopRows";

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function CreateColorScaleDialog({ open, onClose }: Props) {
  const { regionTags, refetch } = useLidarClearance();
  const [region, setRegion] = useState("");
  const { stops, setStops, addStop, updateStop, removeStop } = useStopRows([
    newStopRow(),
  ]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setRegion(regionTags[0] ?? "");
      setStops([newStopRow()]);
    }
  }, [open, regionTags, setStops]);

  const canSubmit = region.length > 0 && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await createColorScale(region, stops.map((stop) => stop.color));
      await refetch();
      onClose();
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.createColorScale" });
      toast.error(t.FailedToCreateColorScale);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => (submitting ? undefined : onClose())}
      fullWidth
    >
      <DialogTitle>{t.CreateColorScaleDialogTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label={t.RegionLabel}
            value={region}
            onChange={(event) => setRegion(event.target.value)}
            disabled={submitting}
          >
            {regionTags.map((tag) => (
              <MenuItem key={tag} value={tag}>
                {tag}
              </MenuItem>
            ))}
          </TextField>
          {stops.map((stop, index) => (
            <Stack
              key={stop.key}
              direction="row"
              spacing={1}
              sx={{ alignItems: "center" }}
            >
              <StopValueFields
                position={index + 1}
                color={stop.color}
                onColorChange={(color) => updateStop(stop.key, color)}
                disabled={submitting}
              />
              <IconButton
                size="small"
                aria-label={t.RemoveStop}
                onClick={() => removeStop(stop.key)}
                disabled={submitting || stops.length === 1}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addStop}
            disabled={submitting}
            sx={{ alignSelf: "flex-start" }}
          >
            {t.AddStopButton}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t.CancelButton}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {t.CreateButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
